import React, { useState, useEffect, useCallback } from 'react';
import { clinicService } from '../../../services/api';

const Step3_SelectDoctors = ({ clinicId, onBack, onNext, error, setError }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      if (!clinicId) return;
      setLoading(true);
      try {
        const data = await clinicService.getClinicDoctors(clinicId);
        setDoctors(data.doctors || []);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to load doctors');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clinicId, setError]);

  const handleToggle = useCallback((doctorId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(doctorId)) next.delete(doctorId);
      else next.add(doctorId);
      return next;
    });
  }, []);

  const handleApplyAll = useCallback(() => {
    setSelectedIds(new Set(doctors.map(d => d.id)));
  }, [doctors]);

  const handleClearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleNext = useCallback(() => {
    if (selectedIds.size === 0) {
      setError('Please select at least one doctor');
      return;
    }
    setError('');
    const selectedDoctors = doctors.filter(d => selectedIds.has(d.id));
    onNext({
      selectedDoctorIds: Array.from(selectedIds),
      doctors: selectedDoctors
    });
  }, [selectedIds, doctors, onNext, setError]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
        <p className="text-gray-600">Loading clinic doctors...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 3: Select Doctors</h2>
        <p className="text-gray-600">
          Tick which doctors should receive this schedule. {selectedIds.size} of {doctors.length} selected.
        </p>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleApplyAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            Apply All
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
          >
            Clear All
          </button>
        </div>
        <span className="text-sm text-gray-600">
          {selectedIds.size} of {doctors.length} selected
        </span>
      </div>

      <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
        {doctors.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No doctors in this clinic</div>
        ) : (
          doctors.map((doctor) => (
            <label
              key={doctor.id}
              className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(doctor.id)}
                onChange={() => handleToggle(doctor.id)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <div>
                <span className="font-medium text-gray-900">{doctor.primary_name}</span>
                {doctor.speciality && (
                  <span className="text-gray-500 text-sm ml-2">- {doctor.speciality}</span>
                )}
              </div>
            </label>
          ))
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={selectedIds.size === 0}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Next ({selectedIds.size} doctors)
        </button>
      </div>
    </div>
  );
};

export default Step3_SelectDoctors;
